import { useState, useRef, useCallback } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, GripVertical, ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const SUPPORTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 20;

interface DesignImageUploadProps {
  walkthroughId: string;
  disabled?: boolean;
}

export function DesignImageUpload({
  walkthroughId,
  disabled,
}: DesignImageUploadProps) {
  const utils = trpc.useUtils();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const { data: images, isLoading } =
    trpc.walkthrough.listDesignImages.useQuery(
      { walkthroughId },
      { enabled: !!walkthroughId },
    );

  const uploadMutation = trpc.walkthrough.uploadDesignImage.useMutation({
    onSuccess: () => {
      utils.walkthrough.listDesignImages.invalidate({ walkthroughId });
    },
    onError: (err) => toastTrpcError(err, "Failed to upload design image"),
  });

  const deleteMutation = trpc.walkthrough.deleteDesignImage.useMutation({
    onSuccess: () => {
      toast.success("Image removed");
      utils.walkthrough.listDesignImages.invalidate({ walkthroughId });
    },
    onError: (err) => toastTrpcError(err, "Failed to delete design image"),
  });

  const validateAndUpload = useCallback(
    (files: FileList | File[]) => {
      const currentCount = images?.length ?? 0;
      const fileArray = Array.from(files);

      if (currentCount + fileArray.length > MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images per walkthrough`);
        return;
      }

      for (const file of fileArray) {
        if (!SUPPORTED_TYPES.includes(file.type)) {
          toast.error(
            `Unsupported format: ${file.name}. Use PNG, JPG, or WEBP.`,
          );
          continue;
        }
        if (file.size > MAX_SIZE_BYTES) {
          toast.error(`${file.name} exceeds 5MB limit`);
          continue;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(",")[1];
          uploadMutation.mutate({
            walkthroughId,
            filename: file.name,
            data: base64,
          });
        };
        reader.readAsDataURL(file);
      }
    },
    [images, walkthroughId, uploadMutation],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      validateAndUpload(e.dataTransfer.files);
    },
    [disabled, validateAndUpload],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        validateAndUpload(e.target.files);
        e.target.value = "";
      }
    },
    [validateAndUpload],
  );

  const imageList = images ?? [];

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      {!disabled && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors",
            dragOver
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50",
          )}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <div className="text-center">
            <p className="text-sm font-medium">
              Drop design images here or click to browse
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, WEBP up to 5MB. Max {MAX_IMAGES} images.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.webp"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}

      {/* Image grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="aspect-video animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      ) : imageList.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {imageList.map((img) => {
            const meta = img.metadata as Record<string, unknown> | null;
            const filename = (meta?.originalFilename as string) ?? "Image";
            const ordering = (meta?.ordering as number) ?? 0;

            return (
              <div
                key={img.id}
                className="group relative aspect-video rounded-lg border bg-muted/50 overflow-hidden"
              >
                <div className="flex h-full items-center justify-center">
                  <ImageIcon
                    className="h-8 w-8 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 border-t bg-background px-2 py-1">
                  <div className="flex items-center gap-1">
                    <GripVertical
                      className="h-3 w-3 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="truncate text-xs font-medium tabular-nums text-foreground">
                      {ordering}. {filename}
                    </span>
                  </div>
                </div>
                {!disabled && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove image"
                    onClick={() =>
                      deleteMutation.mutate({
                        walkthroughId,
                        artifactId: img.id,
                      })
                    }
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : !disabled ? null : (
        <p className="text-sm text-muted-foreground">
          No design images uploaded.
        </p>
      )}

      {uploadMutation.isPending && (
        <p className="text-xs text-muted-foreground animate-pulse">
          Uploading...
        </p>
      )}
    </div>
  );
}
